import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Box, useTheme } from '@mui/material';
import { MonthlyCashflowData } from '@inversor-hub/types';
import { formatCurrency } from '@/utils/misc';

interface CashflowChartProps {
  data: MonthlyCashflowData[];
  height?: number;
}

function formatMonth(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number);
  return new Intl.DateTimeFormat('es-ES', {
    month: 'short',
    year: '2-digit',
  }).format(new Date(year, monthNumber - 1, 1));
}

export default function CashflowChart({ data, height = 350 }: CashflowChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!chartRef.current) return;

    const container = chartRef.current;
    const chart = echarts.init(container);

    chart.setOption({
      backgroundColor: 'transparent',
      legend: {
        top: 4,
        left: 'center',
        itemGap: 24,
        textStyle: {
          color: theme.palette.text.primary,
          fontSize: 12,
          fontWeight: 500,
        },
        data: ['Ingresos', 'Gastos', 'Cashflow neto'],
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: theme.palette.action.hover,
          },
        },
        formatter: (params: unknown) => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const row = data[params[0].dataIndex];
          const values = params
            .map((param) => {
              const valueInCents = Math.round(Number(param.value) * 100);
              return `
                <div style="display:flex;align-items:center;gap:8px;margin-top:6px">
                  <span style="width:9px;height:9px;border-radius:2px;background:${param.color}"></span>
                  <span style="flex:1">${param.seriesName}</span>
                  <strong>${formatCurrency(valueInCents)}</strong>
                </div>
              `;
            })
            .join('');

          return `
            <div style="min-width:180px">
              <strong>${formatMonth(row.month)}</strong>
              ${values}
            </div>
          `;
        },
      },
      grid: {
        left: '3%',
        right: '3%',
        bottom: '8%',
        top: 54,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.map((item) => formatMonth(item.month)),
        axisTick: { show: false },
        axisLine: {
          lineStyle: { color: theme.palette.divider },
        },
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Importe (€)',
        nameTextStyle: {
          color: theme.palette.text.secondary,
        },
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 11,
          formatter: (value: number) =>
            value >= 1000 ? `€${(value / 1000).toFixed(0)}k` : `€${value.toFixed(0)}`,
        },
        splitLine: {
          lineStyle: {
            color: theme.palette.divider,
            type: 'dashed',
          },
        },
      },
      series: [
        {
          name: 'Ingresos',
          type: 'bar',
          data: data.map((item) => item.income / 100),
          barMaxWidth: 28,
          itemStyle: {
            color: theme.palette.success.main,
            borderRadius: [5, 5, 0, 0],
          },
        },
        {
          name: 'Gastos',
          type: 'bar',
          data: data.map((item) => item.expenses / 100),
          barMaxWidth: 28,
          itemStyle: {
            color: theme.palette.error.main,
            borderRadius: [5, 5, 0, 0],
          },
        },
        {
          name: 'Cashflow neto',
          type: 'line',
          data: data.map((item) => item.netCashflow / 100),
          symbol: 'circle',
          symbolSize: 7,
          smooth: 0.25,
          z: 3,
          lineStyle: {
            width: 3,
            color: theme.palette.primary.main,
          },
          itemStyle: {
            color: theme.palette.primary.main,
            borderColor: theme.palette.background.paper,
            borderWidth: 2,
          },
        },
      ],
    });

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [data, height, theme]);

  return (
    <Box sx={{ width: '100%', height }}>
      <div ref={chartRef} style={{ width: '100%', height: '100%' }} />
    </Box>
  );
}
